import type {
  BillingDataT,
  CalculatePurchasePricePayloadT,
  PackageTypeRouteT,
  PurchaseParamsT,
} from "../models/models";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectActiveJMPlans,
  selectCompletedStep,
  selectCouponCode,
  selectIsFetchingActiveJMPlans,
  selectIsFetchingPurchasePrice,
  selectJumboCodeQuantity,
  selectPaymentType,
  selectPurchasePrise,
} from "../store/purchasesSelectors";
import { useCallback } from "react";
import {
  PaymentTypeE,
  PlanPackagesE,
  PurchaseStepE,
  SelectedOptionE,
} from "../models/enums";
import {
  setBillingData,
  setCompletedStep,
  setJumboCodeQuantity,
} from "../store/purchasesSlice";
import getOptionList from "../helpers/getOptionList";
import {
  calculatePurchasePrice,
  fetchActiveJMPlans,
} from "../store/purchasesThunks";
import getPrices from "../helpers/getPrices";
import useCurrency from "./useCurrency";
import { useUser } from "../../../../contexts/userContext/userContext";
import { useModal } from "../../../../contexts/modalContext/modalContext";
import Login from "../../../Structure/Login/Login";
import { useToasts } from "react-toast-notifications";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import useDowngrade from "./useDowngrade";
import DowngradeModal from "../../Purchases/ErrorModals/DowngradeModal";
import { externalValidateDowngradeSubscriptions } from "../../Purchases/Helper";
import useAddon from "./useAddon";
import { UserDataT } from "../../../../models/models";
import UpdateYearlyToMonthlyDenied from "../modals/UpdateYearlyToMonthlyDenied";
import getCorrectBillingMethod from "../helpers/getCorrectBillingMethod";
import PayPalSubUpgradeDenied from "../modals/PayPalSubUpgradeDenied";

/**
 * General purchases logic
 */

const usePurchases = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { packageType, periodType } = useParams<PurchaseParamsT>();
  const { setModal } = useModal();
  const { addToast } = useToasts();
  const { getTextByKey, getHtmlByKey, getLanguage } = useContent();
  const {
    isLoggedIn,
    getUserData,
    getUserBillingDetails,
    isPro,
    isBusiness,
    isPremium,
  } = useUser();
  const dispatch = useAppDispatch();
  const { existingAddonQuantity, addonQuantity } = useAddon();
  const couponCode = useAppSelector(selectCouponCode);
  const jumboCodeQuantity = useAppSelector(selectJumboCodeQuantity);
  const isFetchingPurchasePrice = useAppSelector(selectIsFetchingPurchasePrice);
  const paymentType = useAppSelector(selectPaymentType);
  const { currency } = useCurrency();
  const {
    validateDowngradeAddons,
    validateDowngradeSubscriptions,
    isDowngrade,
  } = useDowngrade();

  const completedStep = useAppSelector(selectCompletedStep);

  // =========================== OPTIONS ============================== //

  // Uses for default plans purchase page
  // and for JUMBOcodes purchase page
  const options = getOptionList(packageType as PackageTypeRouteT);

  const selectOption = useCallback(
    (option: SelectedOptionE) => {
      dispatch(setCompletedStep(PurchaseStepE.PRIMARY_PURCHASE_STEP));

      if (packageType === "pay-as-you-go") {
        // Set JUMBOcodes amount option (5, 10 and 20).
        dispatch(setJumboCodeQuantity(option as number));
      } else {
        if (periodType) {
          // Set period type option (monthly or early)
          navigate(location.pathname.replace(periodType!, option as string));
        } else {
          navigate(location.pathname + "/" + option);
        }
      }
    },
    [dispatch, completedStep]
  );

  const isOptionSelected = (option: SelectedOptionE) => {
    if (packageType === "pay-as-you-go") {
      // Value of selected amount of codes stores in the redux store
      return option === jumboCodeQuantity;
    } else {
      // Another selected options stores within URL
      return option === periodType;
    }
  };

  // Use this function on page mount
  // in order to set default selected option based on pathname
  const setDefaultSelectedOption = useCallback(() => {
    if (packageType === "pay-as-you-go") {
      // If JUMBOcodes payment page
      if (!jumboCodeQuantity) {
        dispatch(setJumboCodeQuantity(SelectedOptionE.TEN_CODES));
      }
    } else if (packageType !== "sub-users" && packageType !== "extra-storage") {
      if (periodType) {
        // dispatch(setSelectedOption(periodType as SelectedOptionE));
      }
    }
  }, [dispatch, jumboCodeQuantity, packageType, periodType]);

  // =========================== PLANS ================================= //

  // Details about active JUMBOmail plans (pro, business and premium).
  const plans = useAppSelector(selectActiveJMPlans);

  const fetchPlans = useCallback(() => {
    dispatch(fetchActiveJMPlans());
  }, [dispatch]);

  const getPlanByName = useCallback(
    (name: PlanPackagesE) => {
      return plans?.find((plan) => plan.Subscription_SystemName === name);
    },
    [plans]
  );

  // Calculated price based on fetched active JM plans data
  // without discount
  const _calcSelectedPlanPrice = useCallback(
    (planName: PlanPackagesE) => {
      let res;
      const currPlan = getPlanByName(planName);
      if (currPlan) {
        const currPlanPrices = currPlan.Prices;
        const prices = getPrices(currPlanPrices, currency);

        if (prices) {
          res = prices[periodType as SelectedOptionE];
        }
      }
      return res;
    },
    [currency, getPlanByName, periodType]
  );

  // Price for selected plan for chosen period
  const selectedPlanPrice = _calcSelectedPlanPrice(
    packageType!! as PlanPackagesE
  );

  // Price for the plan for chosen period user is using now
  const currentPlanPrice = _calcSelectedPlanPrice(
    getUserData()?.userSubscriberSystemName as unknown as PlanPackagesE
  );

  // ============================================================= //

  const updateCompletedStep = useCallback(
    (nextStep: PurchaseStepE) => {
      dispatch(setCompletedStep(nextStep));
    },
    [dispatch]
  );

  const goToSecondStep = () => {
    if ((getUserData() as UserDataT)?.IsSubUser) {
      addToast(<span>{getHtmlByKey("sub-user-select-plan-tooltip")}</span>, {
        appearance: "error",
      });
      return;
    }

    // If selected option value equal to 5, 10 or 20
    const isJUMBOCodes =
      periodType! === "5" || periodType! === "10" || periodType! === "20";
    if (paymentType) {
      const loginValidate = isLoggedIn() || isJUMBOCodes;
      if (loginValidate) {
        let validated = true;
        if (packageType === "sub-users" || packageType === "extra-storage") {
          validated = validateDowngradeAddons();
        } else if (!isJUMBOCodes) {
          validated = validateDowngradeSubscriptions();
        }
        if (validated) {
          updateCompletedStep(PurchaseStepE.FIRST_PURCHASE_METHOD_STEP);

          // Scroll to the next step section after it appeared
          setTimeout(() => {
            const nextStepSection = document.getElementById(
              "billing-info-section"
            );
            if (nextStepSection) {
              // Scroll with top offset
              window.scrollTo({
                top: nextStepSection.offsetTop - 100,
                behavior: "smooth",
              });
            }
          }, 100);
        }
      } else {
        setModal({
          modal: Login,
          modalSize: "full-modal",
        });
      }
    } else {
      addToast(getTextByKey("empty-payment-method"), {
        appearance: "error",
      });
    }
  };

  const goToThirdStep = (payload: BillingDataT) => {
    // Put data from form to store
    dispatch(setBillingData(payload));

    const userData = getUserData();
    const billingDetails = getUserBillingDetails();
    const isSubscriptionPurchase =
      packageType !== "sub-users" &&
      packageType !== "extra-storage" &&
      packageType !== "pay-as-you-go";

    // const isChangePaymentMethodAllowed = isSubscriptionPurchase
    //   ? checkIfChangePayMethodAllowed(paymentType!)
    //   : true;

    // if (!isChangePaymentMethodAllowed) {
    //   return false;
    // }

    // Not allow to upgrade subscription via PayPal
    // if (
    //   isSubscriptionPurchase &&
    //   !isDowngrade &&
    //   billingDetails?.payment_gateway?.toLowerCase() === PaymentTypeE.PAYPAL
    // ) {
    //   setModal({
    //     modal: PayPalSubUpgradeDenied,
    //     modalSize: "full-modal",
    //   });
    //   return;
    // }

    if (isSubscriptionPurchase) {
      const resultValidation = externalValidateDowngradeSubscriptions({
        currentUserDetails: {
          billingMethod: billingDetails
            ? getCorrectBillingMethod(billingDetails.payment_gateway)
            : "",
          billingPeriod: billingDetails?.period || 0,
          userPlan: isPro()
            ? "pro"
            : isBusiness()
            ? "business"
            : isPremium()
            ? "premium"
            : "",
          userExtraStorage: userData?.NumOfExtraStorage,
          userActualStorage: userData?.UserUsedStorage,
          userSubUsersLicneses: userData?.UserTotalSubUsersLicenses,
          numOfForms: userData?.JumboFormsCount,
          IsFreeProFromCoupon: userData?.IsFreeProFromCoupon,
          isSubscriptionInactive: billingDetails?.status === "INACTIVE",
          autoRenew: billingDetails?.auto_renew,
          token_id: billingDetails?.token_id,
          provider_id: billingDetails?.provider_id,
        },
        purchaseDetails: {
          paymentMethod: paymentType,
          period: periodType,
          plan: packageType,
        },
      });

      if (!resultValidation.allowPurchase) {
        if (resultValidation.toastMessage) {
          addToast(getTextByKey(resultValidation.toastMessage), {
            appearance: "error",
          });
          if (
            resultValidation.toastMessage === "cannot-change-yearly-to-monthly"
          ) {
            setModal({
              modal: UpdateYearlyToMonthlyDenied,
              modalSize: "full-modal",
            });
          } else {
            addToast(getTextByKey(resultValidation.toastMessage), {
              appearance: "error",
            });
          }
        } else {
          setModal({
            modal: DowngradeModal,
            modalSize: "full-modal",
            props: {
              errorType: resultValidation.modalName,
            },
          });
        }
      } else {
        updateCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP);
      }
    } else if (packageType === "sub-users" || packageType === "extra-storage") {
      updateCompletedStep(PurchaseStepE.SECOND_INVOICE_DETAILS_STEP);
    } else if (packageType === "pay-as-you-go") {
      // If packageType relates to addons, set third step as completed
      updateCompletedStep(PurchaseStepE.THIRD_PAYMENT_STEP);
    }
  };

  const fetchPurchasePrice = useCallback((packageType: PackageTypeRouteT) => {
    // For pay-as-you-go set quantity from selected options (5, 10 or 20 codes)
    const quantity =
      (packageType === "pay-as-you-go"
        ? jumboCodeQuantity
        : addonQuantity - existingAddonQuantity) || 0;

    // If current package is subscription plan, set period as "yearly" or "monthly"
    const packagePeriod =
      packageType === "business" ||
      packageType === "premium" ||
      packageType === "pro"
        ? (periodType! as SelectedOptionE)
        : null;

    const payload: CalculatePurchasePricePayloadT = {
      packageName: packageType,
      userId: getUserData().UserId,
      currency,
      packagePeriod,
      couponCode: couponCode?.code,
      quantity,
    };
    dispatch(calculatePurchasePrice(payload));
  }, []);

  const purchasePrice = useAppSelector(selectPurchasePrise);
  const isFetchingActiveJMPlans = useAppSelector(selectIsFetchingActiveJMPlans);

  const redirectToCorrectPurchasePage = () => {
    // Redirect from old link
    if (window.location.href.includes("codes")) {
      navigate(`/${getLanguage()}/purchase/pay-as-you-go`);
    }
    // Only for subscriptions if period type is absent or incorrect
    if (
      (packageType === "business" ||
        packageType === "pro" ||
        packageType === "premium") &&
      periodType !== "yearly" &&
      periodType !== "monthly"
    ) {
      if (!periodType) {
        navigate(location.pathname + "/yearly");
        return;
      }

      // Remove last path section from url and add default period type
      const pathWithoutPeriod = location.pathname.split("/");
      pathWithoutPeriod.pop();
      navigate(pathWithoutPeriod.join("/") + `/${SelectedOptionE.YEARLY}`);
    }
  };

  return {
    plans,
    fetchPlans,
    getPlanByName,
    options,
    periodType,
    selectOption,
    isOptionSelected,
    setDefaultSelectedOption,
    selectedPlanPrice,
    currentPlanPrice,
    completedStep,
    setCompletedStep: updateCompletedStep,
    goToSecondStep,
    goToThirdStep,
    fetchPurchasePrice,
    purchasePrice,
    isFetchingActiveJMPlans,
    redirectToCorrectPurchasePage,
    jumboCodeQuantity,
    isFetchingPurchasePrice,
  };
};

export default usePurchases;
